import { Info, Loader2, Users, X } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import type { StudyGroupSelectorProps } from "@/features/chat/types";
import { useMyStudyGroups } from "@/hooks/useMyStudyGroups";

export const StudyGroupSelector = ({
  selectedGroupId,
  onGroupSelect,
}: StudyGroupSelectorProps) => {
  const { t } = useTranslation();
  const { data: studyGroups, isLoading: isLoadingStudyGroups } =
    useMyStudyGroups();
  const [isNoticeOpen, setIsNoticeOpen] = useState(false);

  return (
    <div>
      {selectedGroupId ? (
        <div className="flex items-center gap-2">
          {isLoadingStudyGroups ? (
            <div className="flex items-center py-2 text-sm text-rw-dark-gray">
              <Loader2 size={16} className="mr-2 animate-spin" />
              {t("common.loading")}
            </div>
          ) : (
            <>
              <div className="mb-1 text-xs font-medium">
                {t("chat.selectedGroupLabel")}
              </div>
              <div className="flex flex-wrap items-center gap-2">
                {studyGroups
                  ?.filter((group) => group.member_chat_enabled)
                  .filter((group) => selectedGroupId === group.id.toString())
                  .map((group) => (
                    <div key={group.id} className="flex items-center gap-2">
                      <div className="inline-flex items-center rounded-md border border-input bg-secondary px-3 py-1 text-sm text-secondary-foreground">
                        <Users className="mr-1 size-4" />
                        {group.name}
                        <Button
                          type="button"
                          variant="ghost"
                          size="mini"
                          iconSize="sm"
                          className="ml-1"
                          aria-label={t("common.close")}
                          onClick={() => onGroupSelect("")}
                        >
                          <X className="size-3.5" aria-hidden="true" />
                        </Button>
                      </div>
                    </div>
                  ))}
              </div>
            </>
          )}
        </div>
      ) : (
        <>
          <div className="mb-1 flex items-center gap-1">
            <p className="text-xs font-medium">
              {t("chat.selectableStudyGroupsLabel")}
            </p>
            <TooltipProvider delayDuration={150}>
              <Tooltip open={isNoticeOpen}>
                <TooltipTrigger asChild>
                  <Button
                    type="button"
                    variant="ghost"
                    size="mini"
                    iconSize="sm"
                    className="text-muted-foreground"
                    aria-label={t("chat.selectStudyGroupNotice")}
                    onMouseEnter={() => setIsNoticeOpen(true)}
                    onMouseLeave={() => setIsNoticeOpen(false)}
                    onClick={() => setIsNoticeOpen((prev) => !prev)}
                    onBlur={() => setIsNoticeOpen(false)}
                  >
                    <Info className="size-3.5" aria-hidden="true" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent className="max-w-xs text-xs">
                  {t("chat.selectStudyGroupNotice")}
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
          <div className="max-h-40 overflow-y-auto pr-1">
            <div className="flex flex-wrap gap-2">
              {isLoadingStudyGroups ? (
                <div className="flex items-center py-2 text-sm text-rw-dark-gray">
                  <Loader2 size={16} className="mr-2 animate-spin" />
                  {t("common.loading")}
                </div>
              ) : (
                studyGroups?.map((group) => {
                  if (!group.member_chat_enabled) {
                    return null;
                  }
                  const isSelected = selectedGroupId === group.id.toString();
                  return (
                    <Button
                      key={group.id}
                      variant={isSelected ? "secondary" : "outline"}
                      size="sm"
                      onClick={() => onGroupSelect(group.id.toString())}
                    >
                      <Users className="mr-1 size-4" />
                      {group.name}
                    </Button>
                  );
                })
              )}
            </div>
          </div>
          <p className="mt-2 text-sm font-medium">
            {t("chat.selectStudyGroupDescription")}
          </p>
        </>
      )}
    </div>
  );
};
